import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import MSG from "./constant/message";
import AyaPay from "./components/AyaPay";
import LoadingWithEstimate from "./components/LoadingEstimateTime";
import ErrorMessages from "./components/ErrorMessages";
import KPay from "./components/KPay";
import ReturnPage from "./components/ReturnPage";

// Hooks
import { useQueryParams } from "./hooks/useQueryParams";
import { usePaymentData } from "./hooks/usePaymentData";
import { useQRCountdown } from "./hooks/useQRCountdown";
import { usePaymentSocket } from "./hooks/usePaymentSocket";
import { useReturnUrlPolling } from "./hooks/useReturnUrlPolling";

// Constants
import {
  PaymentMethod,
  QR_OPERATORS,
  IN_APP_OPERATORS,
} from "./constants/payment";
import { MSGMM } from "./constant/message_mmqr";

/**
 * Main component for handling payment redirects and display
 */
export default function FetchAndRedirect() {
  const navigate = useNavigate();
  const qrRef = useRef(null);

  // Environment variables
  const returnUrlApi = process.env.REACT_APP_API_CHECK_STATUS_URL;
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const socketUrl = process.env.REACT_APP_SOCKET_URL;

  // Parse query parameters
  const { lang, orderId } = useQueryParams();

  // Fetch payment data
  const { loading, extraLoading, loadingDuration, err, paymentData } =
    usePaymentData(orderId, baseUrl, navigate);

  const {
    method,
    qrPayload,
    operatorName,
    redirectUrl,
    amount,
    merchantOrderId,
    returnUrl,
    timeLeft: initialTimeLeft,
    status,
  } = paymentData;

  // QR countdown timer
  const timeLeft = useQRCountdown(method, initialTimeLeft);

  // Socket listener for QR payment success
  usePaymentSocket(method, merchantOrderId, socketUrl, returnUrl);

  // Poll for return URL
  useReturnUrlPolling(qrPayload, orderId, returnUrlApi, status);

  // Render states in priority order
  if (err) {
    return <ErrorMessages err={err} returnUrl={returnUrl} />;
  }

  if (loading) {
    return <LoadingWithEstimate lang={lang} duration={loadingDuration} />;
  }

  // Show empty state during extra loading for external redirects
  if (
    extraLoading &&
    method !== PaymentMethod.QR_CODE &&
    method !== PaymentMethod.MMQR &&
    !IN_APP_OPERATORS.includes(operatorName)
  ) {
    return <></>;
  }

  // Render in-app payment operators
  if (IN_APP_OPERATORS.includes(operatorName)) {
    return <AyaPay content={qrPayload} />;
  }

  // Render KBZPay QR payment
  if (
    (method === PaymentMethod.QR_CODE || method === PaymentMethod.MMQR) &&
    operatorName === QR_OPERATORS.KBZPAY
  ) {
    return (
      <KPay
        title={method}
        timeLeft={timeLeft}
        MSG={method === PaymentMethod.MMQR ? MSGMM : MSG}
        lang={lang}
        qrPayload={qrPayload}
        qrRef={qrRef}
        amount={amount}
        merchantOrderId={merchantOrderId}
        orderId={orderId}
      />
    );
  }

  // Default: show return page if redirect URL exists
  return redirectUrl ? <ReturnPage redirectUrl={redirectUrl} /> : null;
}
